import { useRef, useState } from 'react';
import './RedactionChart.css';

const WIDTH = 640;
const HEIGHT = 220;
const MARGIN = { top: 16, right: 12, bottom: 28, left: 44 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const BAR_WIDTH = 24;
const BAR_RADIUS = 4;

function niceCeil(value) {
  if (value <= 0) return 10;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const residual = value / magnitude;
  const niceResidual = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  return niceResidual * magnitude;
}

function monthLabel(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}

function roundedTopBarPath(x, width, top, baseline) {
  const radius = Math.min(BAR_RADIUS, Math.max(0, baseline - top) / 2);
  if (baseline - top <= 0) return '';
  return [
    `M${x},${baseline}`,
    `L${x},${top + radius}`,
    `Q${x},${top} ${x + radius},${top}`,
    `L${x + width - radius},${top}`,
    `Q${x + width},${top} ${x + width},${top + radius}`,
    `L${x + width},${baseline}`,
    'Z',
  ].join(' ');
}

/**
 * Single-series "filtered words per month" bar chart, with a per-bar
 * hover/focus tooltip and a hidden accessible table fallback.
 */
export default function RedactionChart({ data }) {
  const svgRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const maxValue = niceCeil(Math.max(...data.map((d) => d.filteredWordCount), 1));
  const yTicks = [0, maxValue / 2, maxValue];
  const bandWidth = data.length > 0 ? PLOT_WIDTH / data.length : PLOT_WIDTH;
  const baseline = MARGIN.top + PLOT_HEIGHT;

  const bandXFor = (i) => MARGIN.left + bandWidth * i;
  const barXFor = (i) => bandXFor(i) + (bandWidth - BAR_WIDTH) / 2;
  const yFor = (v) => baseline - (v / maxValue) * PLOT_HEIGHT;

  function nearestIndexForClientX(clientX) {
    const rect = svgRef.current.getBoundingClientRect();
    const relativeX = ((clientX - rect.left) / rect.width) * WIDTH;
    const i = Math.floor((relativeX - MARGIN.left) / bandWidth);
    return Math.min(data.length - 1, Math.max(0, i));
  }

  const active = activeIndex != null ? data[activeIndex] : null;
  const maxIndex = data.reduce((best, d, i) => (d.filteredWordCount > data[best].filteredWordCount ? i : best), 0);
  const hasAnyValue = data.some((d) => d.filteredWordCount > 0);

  return (
    <div className="redaction-chart">
      <svg
        ref={svgRef}
        className="redaction-chart-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Filtered words per month in ${data[0]?.month.split('-')[0]}`}
        onPointerMove={(e) => setActiveIndex(nearestIndexForClientX(e.clientX))}
        onPointerLeave={() => setActiveIndex(null)}
      >
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              className="redaction-chart-gridline"
              x1={MARGIN.left}
              x2={WIDTH - MARGIN.right}
              y1={yFor(tick)}
              y2={yFor(tick)}
            />
            <text className="redaction-chart-axis-label" x={MARGIN.left - 8} y={yFor(tick)} textAnchor="end" dy="0.32em">
              {Math.round(tick).toLocaleString()}
            </text>
          </g>
        ))}

        {data.map((d, i) => (
          <rect
            key={`hit-${d.month}`}
            className="redaction-chart-hit"
            x={bandXFor(i)}
            y={MARGIN.top}
            width={bandWidth}
            height={PLOT_HEIGHT}
            tabIndex={0}
            role="button"
            aria-label={`${monthLabel(d.month)}: ${d.filteredWordCount.toLocaleString()} filtered words`}
            onFocus={() => setActiveIndex(i)}
            onBlur={() => setActiveIndex(null)}
          />
        ))}

        {data.map((d, i) => (
          <path
            key={d.month}
            className={`redaction-chart-bar ${activeIndex === i ? 'redaction-chart-bar--active' : ''}`}
            d={roundedTopBarPath(barXFor(i), BAR_WIDTH, yFor(d.filteredWordCount), baseline)}
            pointerEvents="none"
          />
        ))}

        {hasAnyValue && (
          <text
            className="redaction-chart-cap-label"
            x={barXFor(maxIndex) + BAR_WIDTH / 2}
            y={yFor(data[maxIndex].filteredWordCount) - 8}
            textAnchor="middle"
          >
            {data[maxIndex].filteredWordCount.toLocaleString()}
          </text>
        )}

        {data.map((d, i) => (
          <text
            key={`x-${d.month}`}
            className="redaction-chart-axis-label"
            x={bandXFor(i) + bandWidth / 2}
            y={HEIGHT - 8}
            textAnchor="middle"
          >
            {monthLabel(d.month)}
          </text>
        ))}
      </svg>

      {active && (
        <div
          className="redaction-chart-tooltip"
          style={{
            left: `${((barXFor(activeIndex) + BAR_WIDTH / 2) / WIDTH) * 100}%`,
            top: `${(yFor(active.filteredWordCount) / HEIGHT) * 100}%`,
          }}
        >
          <div className="redaction-chart-tooltip-value">{active.filteredWordCount.toLocaleString()} words</div>
          <div className="redaction-chart-tooltip-label">{monthLabel(active.month)}</div>
        </div>
      )}

      <table className="redaction-chart-table sr-only">
        <caption>Filtered words per month</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">Filtered words</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.month}>
              <td>{monthLabel(d.month)}</td>
              <td>{d.filteredWordCount.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
